import {
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  AfterViewChecked,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ConfigService } from 'src/app/services/config.service';
import { renderAgentMarkdown } from 'src/app/utils/agent-markdown';
import { AgentAsnIntakeComponent } from '../agent-asn-intake/agent-asn-intake.component';
import { HubspotDragComponent } from '../hubspot-drag/hubspot-drag.component';

interface ChatMessage {
  type: 'send' | 'receive';
  message: string;
  html?: string;
  feedback?: '' | 'good' | 'bad';
  messageId?: number;
  // While the answer is streaming: what the assistant is doing ("Checking inventory…").
  status?: string;
  isStreaming?: boolean;
}

/** Where the user dropped the launcher: distance from the bottom and from the nearer side edge. */
interface LauncherPosition {
  bottom: number;
  side: 'left' | 'right';
  offset: number;
}

interface AgentStatus {
  enabled: boolean;
  can_chat: boolean;
  can_intake: boolean;
  can_view_logs: boolean;
}

/**
 * The WizeHelp chat panel, backed by the AI agent in the WMS (/api/agent/*). The launcher only
 * appears when the agent is switched on for the tenant and the user's role allows it.
 */
@Component({
  selector: 'app-chatbot',
  templateUrl: './chatbot.component.html',
  styleUrls: ['./chatbot.component.scss'],
})
export class ChatbotComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('scrollMe', { static: false }) private scrollContainer: ElementRef;
  @ViewChild('launcher', { static: false }) private launcher: ElementRef<HTMLElement>;

  // v2: spots saved before the menu limit existed (some under the left menu) are dropped.
  private static readonly POSITION_KEY = 'wizehelp_launcher_position_v2';
  private static readonly EDGE_GAP = 8;
  /** Launcher width when hovered ("Ask WizeHelp" pill), so it never opens off screen. */
  private static readonly LAUNCHER_WIDTH = 150;
  private static readonly LAUNCHER_HEIGHT = 50;
  private static readonly PANEL_WIDTH = 330;
  private static readonly PANEL_HEIGHT = 470;

  /** null = default spot (above the HubSpot chat bubble, see the SCSS). */
  position: LauncherPosition | null = null;
  panelStyle: Record<string, string> = {};
  isDragging = false;
  private dragStart: { x: number; y: number; left: number; top: number } | null = null;
  private justDragged = false;

  userImage: any;
  handleScroll: boolean = false;
  confirmationPopup: boolean = false;
  isHover: boolean = false;
  loader: boolean = false;
  isOpen: boolean = false;
  isMaximized: boolean = false;
  isAvailable: boolean = false;
  /** Role allows Document Intake: the 📎 button reads a packing list / PO into an ASN (agent-owned dialog). */
  canIntake: boolean = false;
  icon: string = 'expand';
  public chatbotForm = { message: '' };

  messages: ChatMessage[] = [];
  private conversationId: string | null = null;

  staffQuestions = [
    { question: 'What needs attention today?' },
    { question: 'Which sales orders are back-ordered, and why?' },
    { question: 'Which tasks are still open?' },
  ];

  clientQuestions = [
    { question: 'What is the status of my latest orders?' },
    { question: 'How much stock do I have left?' },
    { question: 'Which inbound shipments are expected?' },
  ];

  private menuWatch: any;

  constructor(private service: ConfigService, private dialog: MatDialog, private zone: NgZone) {
    this.service.profile$.subscribe(profile => {
      this.userImage = profile;
    });
  }

  /** The area fixed elements are placed in (without scrollbars, unlike window.innerWidth). */
  private get viewWidth(): number {
    return document.documentElement.clientWidth || window.innerWidth;
  }

  private get viewHeight(): number {
    return document.documentElement.clientHeight || window.innerHeight;
  }

  get commonQuestions() {
    return this.service.accountTypeAPI === 'client_service' ? this.clientQuestions : this.staffQuestions;
  }

  async ngOnInit(): Promise<void> {
    if (window.innerHeight <= 612) {
      this.icon = 'shrink';
    }
    this.position = this.loadPosition();
    // The left menu can open or widen without a window resize: move the launcher out from under it.
    this.zone.runOutsideAngular(() => {
      this.menuWatch = setInterval(() => this.keepClearOfMenu(), 1000);
    });
    await this.loadStatus();
  }

  ngOnDestroy(): void {
    clearInterval(this.menuWatch);
  }

  private keepClearOfMenu(): void {
    if (!this.launcher || this.dragStart) {
      return;
    }
    const rect = this.launcher.nativeElement.getBoundingClientRect();
    if (rect.width && rect.left < HubspotDragComponent.sidebarRight() + ChatbotComponent.EDGE_GAP) {
      this.zone.run(() => {
        this.position = this.toPosition(rect.left, rect.top);
        this.updatePanelStyle();
      });
    }
  }

  /**
   * Whether the launcher shows. A failed check (server restarting, network blip) is retried a few times so
   * the launcher does not stay hidden until the next page load; a definite "no access" answer is final.
   */
  async loadStatus(retryDelaysMs: number[] = [3000, 10000, 30000]): Promise<void> {
    for (let attempt = 0; ; attempt++) {
      let response: any = null;
      try {
        response = await this.service.getApi('/api/agent/status', undefined, '', 'wms_service');
      } catch {
        response = null;
      }
      const status: AgentStatus | undefined = response?.data;
      if (status) {
        this.isAvailable = !!status.can_chat;
        this.canIntake = !!status.can_intake;
        return;
      }
      if (attempt >= retryDelaysMs.length) {
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, retryDelaysMs[attempt]));
    }
  }

  ngAfterViewChecked(): void {
    this.scrollToBottom();
  }

  toggleOverlay() {
    this.isOpen = !this.isOpen;
    this.isHover = !this.isHover;
    this.updatePanelStyle();
  }

  /** A click that ends a drag does not open the chat. */
  onLauncherClick() {
    if (this.justDragged) {
      this.justDragged = false;
      return;
    }
    this.toggleOverlay();
  }

  get launcherStyle(): Record<string, string> {
    if (!this.position) {
      return {};
    }
    return {
      bottom: this.position.bottom + 'px',
      [this.position.side]: this.position.offset + 'px',
      [this.position.side === 'left' ? 'right' : 'left']: 'auto',
    };
  }

  /** Drag the launcher anywhere on the screen (mouse, pen or touch); the spot is remembered. */
  onLauncherPointerDown(event: PointerEvent) {
    if (event.button !== 0 || !this.launcher) {
      return;
    }
    // Pointer capture: the HubSpot iframe next to the launcher would otherwise swallow the moves.
    HubspotDragComponent.capture(event);
    const rect = this.launcher.nativeElement.getBoundingClientRect();
    this.dragStart = { x: event.clientX, y: event.clientY, left: rect.left, top: rect.top };
  }

  @HostListener('document:pointermove', ['$event'])
  onPointerMove(event: PointerEvent) {
    if (!this.dragStart) {
      return;
    }
    const dx = event.clientX - this.dragStart.x;
    const dy = event.clientY - this.dragStart.y;
    if (!this.isDragging && Math.abs(dx) + Math.abs(dy) < 5) {
      return;
    }
    this.isDragging = true;
    event.preventDefault();
    this.position = this.toPosition(this.dragStart.left + dx, this.dragStart.top + dy);
    this.updatePanelStyle();
  }

  @HostListener('document:pointerup')
  @HostListener('document:pointercancel')
  onPointerUp() {
    if (!this.dragStart) {
      return;
    }
    this.dragStart = null;
    if (this.isDragging) {
      this.isDragging = false;
      this.justDragged = true;
      this.savePosition();
    }
  }

  @HostListener('window:resize')
  onResize() {
    if (this.position && this.launcher) {
      const rect = this.launcher.nativeElement.getBoundingClientRect();
      this.position = this.toPosition(rect.left, rect.top);
    }
    this.updatePanelStyle();
  }

  /** Top-left corner in the viewport → anchored to the nearer side, kept fully on screen. */
  private toPosition(left: number, top: number): LauncherPosition {
    const gap = ChatbotComponent.EDGE_GAP;
    const width = this.viewWidth;
    const height = this.viewHeight;
    const clampedTop = Math.min(Math.max(top, gap), height - ChatbotComponent.LAUNCHER_HEIGHT - gap);
    const bottom = height - clampedTop - ChatbotComponent.LAUNCHER_HEIGHT;
    const collapsed = ChatbotComponent.LAUNCHER_HEIGHT;
    // Never under the left menu (the hovered "Ask WizeHelp" pill included).
    const minLeft = Math.max(HubspotDragComponent.sidebarRight() + gap, gap);
    left = Math.max(left, minLeft);
    if (left + collapsed / 2 > (width + minLeft) / 2) {
      const right = width - left - collapsed;
      return { bottom, side: 'right', offset: Math.min(Math.max(right, gap), Math.max(width - minLeft - ChatbotComponent.LAUNCHER_WIDTH, gap)) };
    }
    return { bottom, side: 'left', offset: Math.min(Math.max(left, minLeft), Math.max(width - ChatbotComponent.LAUNCHER_WIDTH - gap, minLeft)) };
  }

  /** The chat panel opens next to the launcher (above or below it, toward the middle of the screen). */
  private updatePanelStyle() {
    if (!this.launcher || this.isMaximized) {
      this.panelStyle = {};
      return;
    }
    const gap = ChatbotComponent.EDGE_GAP;
    const panel = ChatbotComponent.PANEL_HEIGHT;
    const rect = this.launcher.nativeElement.getBoundingClientRect();
    const height = this.viewHeight;
    const style: Record<string, string> = {};
    let vertical: 'bottom' | 'top' = 'bottom';
    if (rect.top - 10 - panel >= gap) {
      style['top'] = 'auto';
      style['bottom'] = height - rect.top + 10 + 'px';
    } else if (rect.bottom + 10 + panel <= height - gap) {
      vertical = 'top';
      style['bottom'] = 'auto';
      style['top'] = rect.bottom + 10 + 'px';
    } else {
      vertical = 'top';
      style['bottom'] = 'auto';
      style['top'] = gap + 'px';
    }
    const side = this.position?.side ?? 'right';
    const offset = this.position?.offset ?? this.viewWidth - rect.right;
    style[side] = Math.max(offset, gap) + 'px';
    style[side === 'left' ? 'right' : 'left'] = 'auto';
    style['transform-origin'] = `${vertical} ${side}`;
    this.panelStyle = style;
  }

  private loadPosition(): LauncherPosition | null {
    try {
      const saved = JSON.parse(localStorage.getItem(ChatbotComponent.POSITION_KEY) || 'null');
      if (saved && typeof saved.bottom === 'number' && typeof saved.offset === 'number' && ['left', 'right'].includes(saved.side)) {
        const gap = ChatbotComponent.EDGE_GAP;
        return {
          bottom: Math.min(Math.max(saved.bottom, gap), this.viewHeight - ChatbotComponent.LAUNCHER_HEIGHT - gap),
          side: saved.side,
          offset: Math.min(Math.max(saved.offset, gap), this.viewWidth - ChatbotComponent.LAUNCHER_WIDTH - gap),
        };
      }
    } catch {
      // No storage (private window): the default spot is used.
    }
    return null;
  }

  private savePosition() {
    try {
      localStorage.setItem(ChatbotComponent.POSITION_KEY, JSON.stringify(this.position));
    } catch {
      // Not remembered, still moved for this visit.
    }
  }

  toggleMaximize() {
    this.isMaximized = !this.isMaximized;
    // Maximized docks at the default corner; normal size goes back next to the launcher.
    this.updatePanelStyle();
    this.icon = this.icon === 'shrink' ? 'expand' : 'shrink';
    const chatbotContainer = document.querySelector('.chatbot-overlay-container') as HTMLElement;

    if (this.isMaximized) {
        const scale = this.calculateZoom(window.innerHeight);

        if (chatbotContainer) {
            chatbotContainer.style.transform = `scale(${scale})`;
            if (window.innerHeight > 612) {
            chatbotContainer.style.width = `50%`;
            }
        }
    } else {
        if (chatbotContainer) {
            chatbotContainer.style.transform = 'scale(1)';
            chatbotContainer.style.width = `330px`;
        }
    }
  }

  calculateZoom(windowHeight: number): number {
    let scale: number;

    if (windowHeight > 1000) {
        scale = 1.6; // 160% zoom for large screens
    } else if (windowHeight > 800) {
        scale = 1.4;
    } else if (windowHeight > 700) {
        scale = 1.3;
    } else if (windowHeight > 612) {
        scale = 1.2; // 120% zoom for medium screens
    } else  {
        scale = 0.8; // 80% zoom for screens below 612px
    }
    return scale;
  }

  private scrollToBottom(): void {
    try {
      if (this.scrollContainer && this.handleScroll) {
        const container = this.scrollContainer.nativeElement;
        container.scrollTop = container.scrollHeight;
        this.handleScroll = false;
      }
    } catch (err) {
      console.error('Error while scrolling:', err);
    }
  }

  deletePopup() {
    this.confirmationPopup = true;
  }

  /** Clearing the chat starts a new conversation with the agent. */
  deleteChat() {
    this.messages = [];
    this.conversationId = null;
    this.confirmationPopup = !this.confirmationPopup;
  }

  async submitFeedback(i: number, feedback: 'good' | 'bad') {
    const message = this.messages[i];
    if (!message?.messageId) {
      return;
    }
    message.feedback = message.feedback === feedback ? '' : feedback;
    await this.service.postApi('/api/agent/feedback', { message_id: message.messageId, feedback: message.feedback }, undefined, 'wms_service');
  }

  /** 📎: a supplier document → checked draft → ASN created through the normal ASN API. */
  openAsnFromDocument() {
    this.dialog.open(AgentAsnIntakeComponent, { width: '760px', maxWidth: '95vw', autoFocus: false, disableClose: true })
      .afterClosed().subscribe((created: any) => {
        if (created?.asn_no) {
          this.addReply(`**${created.asn_no}** was created from your document. Open it on the ASN page to receive it.`);
        }
      });
  }

  /** A suggested question is asked right away (no second click on Send). */
  commonQues(question: any) {
    this.chatbotForm.message = question;
    this.submit();
  }

  /** Streams the answer: shows what the assistant is looking up, then the text as it is written. */
  async submit() {
    const question = this.chatbotForm.message.trim();
    if (!question || this.loader) {
      return;
    }
    this.messages.push({ type: 'send', message: question });
    this.chatbotForm.message = '';
    this.loader = true;
    this.handleScroll = true;

    const reply: ChatMessage = { type: 'receive', message: '', html: '', feedback: '', status: 'Thinking…', isStreaming: true };
    let shown = false;

    const result = await this.service.streamApi(
      '/api/agent/chat/stream',
      { message: question, conversation_id: this.conversationId },
      'wms_service',
      (event: any) => {
        if (!shown) {
          this.loader = false;
          this.messages.push(reply);
          shown = true;
        }
        if (event.type === 'status') {
          reply.status = event.text;
        } else if (event.type === 'reset') {
          // A lookup replaces text written so far; lists already shown this turn are sent back to keep.
          reply.message = event.text || '';
        } else if (event.type === 'delta') {
          reply.status = '';
          reply.message += event.text;
        } else if (event.type === 'error') {
          // The server hit a problem after the stream started: show its message, not an empty bubble.
          reply.message = event.message || 'Something went wrong. Please try again.';
          reply.status = '';
          reply.isStreaming = false;
        } else if (event.type === 'done') {
          this.conversationId = event.conversation_id;
          reply.message = event.reply;
          reply.messageId = event.message_id;
          reply.status = '';
          reply.isStreaming = false;
        }
        reply.html = renderAgentMarkdown(reply.message);
        this.handleScroll = true;
      }
    );
    this.loader = false;

    if (result.ok && shown) {
      reply.isStreaming = false;
      reply.status = '';
      // The stream closed without an answer (e.g. the server failed mid-way).
      if (!reply.message.trim()) {
        reply.message = 'The assistant could not answer just now. Please try again in a moment.';
        reply.html = renderAgentMarkdown(reply.message);
      }
      return;
    }

    // Error before or during the stream: the agent service puts a user-facing message in error.message.
    if (result.status === 409) {
      this.conversationId = null;
    }
    const message = result.error?.message || 'Something went wrong. Please try again.';
    if (shown) {
      reply.message = message;
      reply.html = renderAgentMarkdown(message);
      reply.status = '';
      reply.isStreaming = false;
    } else {
      this.addReply(message);
    }
  }

  handleImageError(event: any): void {
    event.target.src = '../../../../assets/images/user-avatar.png';  // Set the fallback image
  }

  private addReply(text: string, messageId?: number) {
    this.messages.push({ type: 'receive', message: text, html: renderAgentMarkdown(text), messageId, feedback: '' });
    this.handleScroll = true;
  }
}
